/** Câmera Action RPG: elevada e inclinada, segue o alvo com damping (sem vibrar). */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { clamp, damp } from "../utils/math";

// aspect de referência (retrato ~375x812). Telas mais ESTREITAS afastam a câmera
// (para o Player enxergar ameaças/telegraphs); telas mais largas aproximam um pouco.
const REF_ASPECT = 0.46;

export class CameraRig {
  private readonly baseOffset: THREE.Vector3;
  private readonly offset = new THREE.Vector3();
  private readonly desiredPos = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  private shake = 0; // impulso transitório (sutil)
  /** 10.9AZ — "dolly" de impacto: a câmera dá um passinho pra DENTRO no golpe e volta (peso). */
  private dolly = 0;
  private frameScale = 1;
  private bossZoom = 1; // afasta suavemente durante Boss (ver Boss + player + telegraphs)
  /** 10.6C — distância escolhida pelo jogador nas Configurações (a câmera vinha colada). */
  private userZoom = 1;
  private readonly bossFocus = new THREE.Vector3();
  private hasBossFocus = false;
  private readonly aimFocus = new THREE.Vector3();
  private hasAimFocus = false;
  private readonly _look = new THREE.Vector3();
  private reducedMotion = false;

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    const o = GameConfig.camera.offset;
    this.baseOffset = new THREE.Vector3(o.x, o.y, o.z);
    this.offset.copy(this.baseOffset);
  }

  /** Enquadramento adaptativo por aspect ratio — mantém a área de gameplay útil consistente. */
  setAspect(aspect: number): void {
    this.frameScale = clamp(REF_ASPECT / Math.max(0.2, aspect), 0.85, 1.6);
    this.recomputeOffset();
  }

  /** Zoom de Boss: afasta a câmera para caber o Boss + player + telegraphs (Fase 9). */
  setBossZoom(z: number): void {
    this.bossZoom = z;
    this.recomputeOffset();
  }
  /** 10.6C — distância da câmera escolhida pelo jogador (Configurações → Câmera). */
  setUserZoom(z: number): void {
    this.userZoom = Math.min(1.6, Math.max(0.85, z));
    this.recomputeOffset();
  }
  private recomputeOffset(): void {
    this.offset.copy(this.baseOffset).multiplyScalar(this.frameScale * this.bossZoom * this.userZoom);
  }

  /**
   * 10.6H — FOCO NO ALVO. Com arco (e com a corrente atirando a 9–10 m) dava para estar
   * batendo num monstro que nem aparecia na tela. A câmera agora inclina de leve na direção
   * do alvo atual, o suficiente para ele entrar no enquadramento sem tirar o Caçador do centro.
   */
  setAimFocus(pos: THREE.Vector3 | null): void {
    if (pos) {
      this.aimFocus.copy(pos);
      this.hasAimFocus = true;
    } else {
      this.hasAimFocus = false;
    }
  }

  /** Foco de Boss: mira num ponto entre player e Boss (compõe ambos; Boss não fica atrás da HUD). */
  setBossFocus(bossPos: THREE.Vector3 | null): void {
    if (bossPos) {
      this.bossFocus.copy(bossPos);
      this.hasBossFocus = true;
    } else {
      this.hasBossFocus = false;
    }
  }

  /** Acessibilidade: reduz/desliga o impulso de câmera. */
  setReducedMotion(v: boolean): void {
    this.reducedMotion = v;
    if (v) this.shake = 0;
  }

  /** Posiciona instantaneamente (início / respawn) — sem suavização. */
  snapTo(targetPos: THREE.Vector3): void {
    this.desiredPos.copy(targetPos).add(this.offset);
    this.camera.position.copy(this.desiredPos);
    this.lookTarget.copy(targetPos);
    this.camera.lookAt(this.lookTarget);
  }

  update(targetPos: THREE.Vector3, dt: number): void {
    const pd = GameConfig.camera.positionDamping;
    const ld = GameConfig.camera.lookDamping;

    // ponto de mira: player, ou um ponto enviesado para o Boss (compõe ambos na tela)
    this._look.copy(targetPos);
    if (this.hasBossFocus) this._look.lerp(this.bossFocus, 0.34);
    else if (this.hasAimFocus) this._look.lerp(this.aimFocus, 0.22); // 10.6H: alvo entra na tela

    // 10.9AZ — dolly: encurta o offset por um instante (aproxima) e relaxa exponencialmente
    this.desiredPos.copy(this._look).addScaledVector(this.offset, 1 - this.dolly);
    if (this.dolly > 0.0005) this.dolly *= Math.exp(-7 * dt);
    this.camera.position.x = damp(this.camera.position.x, this.desiredPos.x, pd, dt);
    this.camera.position.y = damp(this.camera.position.y, this.desiredPos.y, pd, dt);
    this.camera.position.z = damp(this.camera.position.z, this.desiredPos.z, pd, dt);

    this.lookTarget.x = damp(this.lookTarget.x, this._look.x, ld, dt);
    this.lookTarget.y = damp(this.lookTarget.y, this._look.y, ld, dt);
    this.lookTarget.z = damp(this.lookTarget.z, this._look.z, ld, dt);

    if (this.shake > 0.001) {
      this.camera.position.x += (Math.random() - 0.5) * this.shake;
      this.camera.position.z += (Math.random() - 0.5) * this.shake;
      this.shake *= Math.exp(-9 * dt);
    }
    this.camera.lookAt(this.lookTarget);
  }

  /** Impulso curto e sutil (impacto de golpe/aterrissagem). Clampado — sem shake forte. */
  impulse(strength: number): void {
    if (this.reducedMotion) return;
    this.shake = Math.min(this.shake + strength, 0.35);
    // 10.9AZ — todo impacto também "puxa" a câmera de leve (máx 6% do offset): peso sem enjoar
    this.dolly = Math.min(this.dolly + strength * 0.18, 0.06);
  }
}
